import { z } from 'zod';

export const JOB_STATUSES = ['Open', 'Onsite', 'Complete', 'Hold'] as const;
export const AUDIT_EVENT_TYPES = [
  'created',
  'updated',
  'status',
  'flow',
  'timer',
  'checklist',
  'note',
  'import',
  'sync',
  'delete',
  'incident',
  'photo',
  'closeout',
  'settings'
] as const;
export const LIBRARY_TAGS = [
  'CombAir',
  'Venting',
  'LeakCheck',
  'Purge',
  'Regulators',
  'MeterSet',
  'Valves',
  'Testing',
  'Appliances',
  'Safety',
  'Manifold',
  'Load',
  'Piping',
  'Odor',
  'Pressure',
  'Bonding',
  'Relight',
  'Code'
] as const;

export const CHECKLIST_LABELS = {
  appliance_connected_or_branch_capped: 'Appliance connected or branch capped',
  disconnected_outlets_capped: 'Disconnected outlets capped',
  manifold_cross_meter_checked: 'Cross-meter check complete',
  manifold_tagged_gs6500_100: 'Manifold tagged per GS6500.100',
  appliance_visual_inspection_gs6500_060: 'Appliance visual inspection complete',
  vents_ok: 'Venting verified',
  meter_sized_for_load: 'Meter sized for load',
  safety_checks_complete: 'Safety checks complete',
  gas_on: 'Gas restored'
} as const;

export type ChecklistKey = keyof typeof CHECKLIST_LABELS;
export type JobStatus = (typeof JOB_STATUSES)[number];
export type LibraryTag = (typeof LIBRARY_TAGS)[number];
export type AuditEventType = (typeof AUDIT_EVENT_TYPES)[number];

export const ChecklistSchema = z.object({
  appliance_connected_or_branch_capped: z.boolean().default(false),
  disconnected_outlets_capped: z.boolean().default(false),
  manifold_cross_meter_checked: z.boolean().default(false),
  manifold_tagged_gs6500_100: z.boolean().default(false),
  appliance_visual_inspection_gs6500_060: z.boolean().default(false),
  vents_ok: z.boolean().default(false),
  meter_sized_for_load: z.boolean().default(false),
  safety_checks_complete: z.boolean().default(false),
  gas_on: z.boolean().default(false)
});

export const TimerWindowSchema = z.object({
  startedAt: z.string().nullable().default(null),
  endedAt: z.string().nullable().default(null),
  totalMinutes: z.number().int().nonnegative().default(0)
});

export const JobPhotoSchema = z.object({
  id: z.string(),
  name: z.string(),
  mimeType: z.string().default('image/jpeg'),
  dataUrl: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  createdAt: z.string(),
  width: z.number().int().positive(),
  height: z.number().int().positive()
});

export const EvidenceItemSchema = z.object({
  id: z.string(),
  kind: z.enum(['note', 'photo', 'document']),
  label: z.string(),
  summary: z.string().default(''),
  addedAt: z.string(),
  externalUrl: z.string().url().optional()
});

export const AuditEventSchema = z.object({
  id: z.string(),
  at: z.string(),
  type: z.enum(AUDIT_EVENT_TYPES),
  detail: z.string(),
  actor: z.enum(['user', 'system']).default('user')
});

export const FlowStepStateSchema = z.enum(['todo', 'in_progress', 'done']);

export const JobSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  date: z.string(),
  territory: z.string().default(''),
  status: z.enum(JOB_STATUSES).default('Open'),
  flowId: z.string().nullable().default(null),
  appointmentType: z.string().default(''),
  premiseStatus: z.string().default(''),
  address: z.string().default(''),
  customerName: z.string().default(''),
  phone: z.string().default(''),
  workOrder: z.string().default(''),
  siteId: z.string().default(''),
  hostOrder: z.string().default(''),
  hostCreatedAt: z.string().default(''),
  meterNumber: z.string().default(''),
  meterLocationCode: z.string().default(''),
  meterLocationDescription: z.string().default(''),
  prevReading: z.number().nullable().default(null),
  currentReading: z.number().nullable().default(null),
  prevReadDate: z.string().default(''),
  travel: TimerWindowSchema.default({ startedAt: null, endedAt: null, totalMinutes: 0 }),
  work: TimerWindowSchema.default({ startedAt: null, endedAt: null, totalMinutes: 0 }),
  checklist: ChecklistSchema.default(() => ({
    appliance_connected_or_branch_capped: false,
    disconnected_outlets_capped: false,
    manifold_cross_meter_checked: false,
    manifold_tagged_gs6500_100: false,
    appliance_visual_inspection_gs6500_060: false,
    vents_ok: false,
    meter_sized_for_load: false,
    safety_checks_complete: false,
    gas_on: false
  })),
  flowStepStates: z.record(z.string(), FlowStepStateSchema).default({}),
  checklistCompletionPercent: z.number().int().min(0).max(100).default(0),
  autoComment: z.string().default(''),
  techNotes: z.string().default(''),
  riskNotes: z.array(z.string()).default([]),
  evidence: z.array(EvidenceItemSchema).default([]),
  photos: z.array(JobPhotoSchema).default([]),
  auditTrail: z.array(AuditEventSchema).default([])
});

export const LibraryCardSchema = z.object({
  id: z.string(),
  title: z.string(),
  when: z.string(),
  steps: z.array(z.string()).default([]),
  fails: z.array(z.string()).default([]),
  refs: z.array(z.string()).default([]),
  tags: z.array(z.enum(LIBRARY_TAGS)).default([]),
  favorite: z.boolean().default(false),
  system: z.boolean().default(false),
  updatedAt: z.string(),
  lastViewedAt: z.string().nullable().default(null)
});

export const IncidentSchema = z.object({
  id: z.string(),
  jobId: z.string().optional(),
  type: z.enum(['safety', 'equipment', 'customer', 'procedural', 'other']),
  severity: z.enum(['low', 'medium', 'high', 'critical']),
  description: z.string(),
  resolution: z.string().optional(),
  tags: z.array(z.string()).default([]),
  createdAt: z.string()
});

export const CustomerExplanationSchema = z.object({
  jobType: z.string(),
  whatWeDid: z.string(),
  whatWeFound: z.string(),
  whatYouNeedToKnow: z.string(),
  safetyNotes: z.array(z.string()).default([]),
  nextSteps: z.string().optional()
});

export const SupervisorSummarySchema = z.object({
  date: z.string(),
  techName: z.string(),
  jobCount: z.number().int().nonnegative(),
  completionRate: z.number().int().min(0).max(100),
  incidents: z.number().int().nonnegative(),
  highlights: z.array(z.string()).default([]),
  concerns: z.array(z.string()).default([])
});

export const BetaFeedbackSchema = z.object({
  id: z.string(),
  version: z.string(),
  buildNumber: z.string(),
  category: z.enum(['bug', 'feature', 'ux', 'performance', 'other']),
  description: z.string(),
  screenshot: z.string().optional(),
  timestamp: z.string(),
  deviceInfo: z.string()
});

export const CrashLogSchema = z.object({
  id: z.string(),
  timestamp: z.string(),
  type: z.enum(['react', 'window', 'promise', 'manual']),
  message: z.string(),
  stack: z.string().optional(),
  componentStack: z.string().optional(),
  route: z.string(),
  userAgent: z.string(),
  appVersion: z.string(),
  buildNumber: z.string(),
  deviceInfo: z.string()
});

export const TrainingReviewEntrySchema = z.object({
  cardId: z.string(),
  reviewedAt: z.string()
});

export const AppSettingsSchema = z.object({
  technicianName: z.string().default(''),
  trainingMode: z.boolean().default(false),
  oneHandMode: z.boolean().default(false),
  highContrastMode: z.boolean().default(false),
  sunlightMode: z.boolean().default(false),
  compactServiceRail: z.boolean().default(false),
  trainingSeenCardIds: z.array(z.string()).default([]),
  trainingReviewLog: z.array(TrainingReviewEntrySchema).default([]),
  dailyCardId: z.string().nullable().default(null),
  dailyCardDate: z.string().default('')
});

export const IntakeFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.string(),
  confidence: z.enum(['high', 'medium', 'low'])
});

export const IntakeParseResultSchema = z.object({
  overallConfidence: z.enum(['high', 'medium', 'low']),
  warnings: z.array(z.string()),
  fields: z.array(IntakeFieldSchema),
  draft: JobSchema.partial()
});

export const DashboardSnapshotSchema = z.object({
  jobCount: z.number().int().nonnegative(),
  activeCount: z.number().int().nonnegative(),
  completedToday: z.number().int().nonnegative(),
  averageReadiness: z.number().int().nonnegative(),
  statusBreakdown: z.array(
    z.object({
      status: z.enum(JOB_STATUSES),
      count: z.number().int().nonnegative()
    })
  ),
  focusJobs: z.array(JobSchema),
  recentSignals: z.array(AuditEventSchema)
});

export const ExportBundleSchema = z.object({
  exportedAt: z.string(),
  version: z.literal('service-companion-v2'),
  jobs: z.array(JobSchema),
  cards: z.array(LibraryCardSchema),
  incidents: z.array(IncidentSchema).default([]),
  settings: AppSettingsSchema.default(() => ({
    technicianName: '',
    trainingMode: false,
    oneHandMode: false,
    highContrastMode: false,
    sunlightMode: false,
    compactServiceRail: false,
    trainingSeenCardIds: [],
    trainingReviewLog: [],
    dailyCardId: null,
    dailyCardDate: ''
  })),
  auditEvents: z.array(AuditEventSchema),
  betaFeedback: z.array(BetaFeedbackSchema).default([]),
  crashLogs: z.array(CrashLogSchema).default([])
});

export const LegacyJobSchema = z.object({
  identity: z
    .object({
      job_id: z.string().optional(),
      date: z.string().optional(),
      territory: z.string().optional(),
      status: z.enum(JOB_STATUSES).optional()
    })
    .optional(),
  service_info: z
    .object({
      appointment_type: z.string().optional(),
      premis_status: z.string().optional(),
      address: z.string().optional(),
      customer_name: z.string().optional(),
      phone: z.string().optional()
    })
    .optional(),
  work_order: z
    .object({
      work_order: z.string().optional(),
      site_id_psid: z.string().optional(),
      host_order_dis: z.string().optional(),
      host_time_created: z.string().optional()
    })
    .optional(),
  meter: z
    .object({
      meter_number: z.string().optional(),
      meter_location_code: z.string().optional(),
      meter_location_desc: z.string().optional(),
      prev_reading: z.number().nullable().optional(),
      current_reading: z.number().nullable().optional(),
      prev_read_date: z.string().optional()
    })
    .optional(),
  timing: z
    .object({
      trv_start: z.string().nullable().optional(),
      trv_end: z.string().nullable().optional(),
      wrk_start: z.string().nullable().optional(),
      wrk_end: z.string().nullable().optional(),
      auto_durations: z
        .object({
          trv_minutes: z.number().nullable().optional(),
          wrk_minutes: z.number().nullable().optional(),
          total_minutes: z.number().nullable().optional()
        })
        .optional()
    })
    .optional(),
  checklist: ChecklistSchema.partial().optional(),
  comments: z
    .object({
      auto_comment: z.string().optional(),
      tech_notes: z.string().optional()
    })
    .optional(),
  audit_timeline: z.array(
    z.object({
      ts: z.string(),
      type: z.string(),
      detail: z.string()
    })
  ).optional()
});

export const LegacyLibraryCardSchema = z.object({
  id: z.string(),
  title: z.string(),
  when: z.string(),
  steps: z.array(z.string()).default([]),
  fails: z.array(z.string()).default([]),
  refs: z.array(z.string()).default([]),
  tags: z.array(z.enum(LIBRARY_TAGS)).default([]),
  favorite: z.boolean().default(false),
  updated_at: z.string()
});

export const LegacyBrowserSnapshotSchema = z.object({
  jobs: z.array(LegacyJobSchema).default([]),
  cards: z.array(LegacyLibraryCardSchema).default([])
});

export type Checklist = z.infer<typeof ChecklistSchema>;
export type TimerWindow = z.infer<typeof TimerWindowSchema>;
export type JobPhoto = z.infer<typeof JobPhotoSchema>;
export type EvidenceItem = z.infer<typeof EvidenceItemSchema>;
export type AuditEvent = z.infer<typeof AuditEventSchema>;
export type FlowStepState = z.infer<typeof FlowStepStateSchema>;
export type Job = z.infer<typeof JobSchema>;
export type LibraryCard = z.infer<typeof LibraryCardSchema>;
export type Incident = z.infer<typeof IncidentSchema>;
export type CustomerExplanation = z.infer<typeof CustomerExplanationSchema>;
export type SupervisorSummary = z.infer<typeof SupervisorSummarySchema>;
export type BetaFeedback = z.infer<typeof BetaFeedbackSchema>;
export type CrashLog = z.infer<typeof CrashLogSchema>;
export type TrainingReviewEntry = z.infer<typeof TrainingReviewEntrySchema>;
export type AppSettings = z.infer<typeof AppSettingsSchema>;
export type IntakeParseResult = z.infer<typeof IntakeParseResultSchema>;
export type DashboardSnapshot = z.infer<typeof DashboardSnapshotSchema>;
export type ExportBundle = z.infer<typeof ExportBundleSchema>;
export type LegacyBrowserSnapshot = z.infer<typeof LegacyBrowserSnapshotSchema>;

export function todayIso(): string {
  return new Date().toISOString();
}

export function todayDate(): string {
  return todayIso().slice(0, 10);
}

export function createId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

export function normalizePhone(phone: string): string {
  return phone.replace(/[^\d+]/g, '').trim();
}

export function createAuditEvent(type: AuditEventType, detail: string, actor: 'user' | 'system' = 'user'): AuditEvent {
  return {
    id: createId('audit'),
    at: todayIso(),
    type,
    detail,
    actor
  };
}

export function appendAudit(job: Job, event: AuditEvent): Job {
  return {
    ...job,
    auditTrail: [...job.auditTrail, event].slice(-160)
  };
}

export function calculateWindowMinutes(window: TimerWindow): number {
  if (!window.startedAt) return 0;
  const end = window.endedAt ? new Date(window.endedAt) : new Date();
  const start = new Date(window.startedAt);
  const minutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  return minutes;
}

export function computeChecklistCompletionPercent(checklist: Checklist): number {
  const values = Object.values(checklist);
  const complete = values.filter(Boolean).length;
  return Math.round((complete / values.length) * 100);
}

export function hydrateJob(job: Job, options: { touch?: boolean } = {}): Job {
  const parsed = JobSchema.parse(job);
  const travelMinutes = calculateWindowMinutes(parsed.travel);
  const workMinutes = calculateWindowMinutes(parsed.work);
  const autoComment = computeAutoComment(parsed.checklist);
  const checklistCompletionPercent = computeChecklistCompletionPercent(parsed.checklist);
  return JobSchema.parse({
    ...parsed,
    updatedAt: options.touch ? todayIso() : parsed.updatedAt,
    autoComment,
    checklistCompletionPercent,
    travel: {
      ...parsed.travel,
      totalMinutes: travelMinutes
    },
    work: {
      ...parsed.work,
      totalMinutes: workMinutes
    }
  });
}

export function createEmptyJob(overrides: Partial<Job> = {}): Job {
  const base: Job = {
    id: createId('job'),
    createdAt: todayIso(),
    updatedAt: todayIso(),
    date: todayDate(),
    territory: '',
    status: 'Open',
    flowId: null,
    appointmentType: '',
    premiseStatus: '',
    address: '',
    customerName: '',
    phone: '',
    workOrder: '',
    siteId: '',
    hostOrder: '',
    hostCreatedAt: '',
    meterNumber: '',
    meterLocationCode: '',
    meterLocationDescription: '',
    prevReading: null,
    currentReading: null,
    prevReadDate: '',
    travel: { startedAt: null, endedAt: null, totalMinutes: 0 },
    work: { startedAt: null, endedAt: null, totalMinutes: 0 },
    checklist: ChecklistSchema.parse({}),
    flowStepStates: {},
    checklistCompletionPercent: 0,
    autoComment: '',
    techNotes: '',
    riskNotes: [],
    evidence: [],
    photos: [],
    auditTrail: []
  };
  return hydrateJob(JobSchema.parse({ ...base, ...overrides }), { touch: true });
}

export function createEmptyLibraryCard(overrides: Partial<LibraryCard> = {}): LibraryCard {
  const base: LibraryCard = {
    id: createId('card'),
    title: '',
    when: '',
    steps: [],
    fails: [],
    refs: [],
    tags: [],
    favorite: false,
    system: false,
    updatedAt: todayIso(),
    lastViewedAt: null
  };
  return LibraryCardSchema.parse({ ...base, ...overrides });
}

export function createEmptyIncident(overrides: Partial<Incident> = {}): Incident {
  return IncidentSchema.parse({
    id: createId('incident'),
    type: 'safety',
    severity: 'medium',
    description: '',
    tags: [],
    createdAt: todayIso(),
    ...overrides
  });
}

export function createDefaultSettings(overrides: Partial<AppSettings> = {}): AppSettings {
  return AppSettingsSchema.parse(overrides);
}

export function computeAutoComment(checklist: Checklist): string {
  const tokens: string[] = [];
  if (checklist.appliance_connected_or_branch_capped) tokens.push('CGI OK');
  if (checklist.disconnected_outlets_capped) tokens.push('SS OK');
  if (checklist.manifold_cross_meter_checked) tokens.push('CSL OK');
  if (checklist.manifold_tagged_gs6500_100) tokens.push('LU/FL OK', 'HLPT OK');
  if (checklist.vents_ok) tokens.push('VNT OK');
  if (checklist.meter_sized_for_load) tokens.push('MTRSZ OK');
  if (checklist.appliance_visual_inspection_gs6500_060) tokens.push('CYCL OK');
  if (checklist.safety_checks_complete) tokens.push('SFTY CMPL');
  if (checklist.gas_on) tokens.push('GAS ON');
  return tokens.join(' ');
}

export function computeReadinessScore(job: Job): number {
  const weighted: [ChecklistKey, number][] = [
    ['safety_checks_complete', 18],
    ['gas_on', 14],
    ['appliance_connected_or_branch_capped', 12],
    ['vents_ok', 12],
    ['meter_sized_for_load', 12],
    ['appliance_visual_inspection_gs6500_060', 10],
    ['manifold_cross_meter_checked', 8],
    ['manifold_tagged_gs6500_100', 7],
    ['disconnected_outlets_capped', 7]
  ];
  const checklistScore = weighted.reduce((sum, [key, weight]) => sum + (job.checklist[key] ? weight : 0), 0);
  const flowStates = Object.values(job.flowStepStates);
  const flowScore = flowStates.length === 0 ? 0 : Math.round((flowStates.filter((state) => state === 'done').length / flowStates.length) * 12);
  const structureScore =
    (job.workOrder ? 8 : 0) +
    (job.address ? 8 : 0) +
    (job.customerName ? 4 : 0) +
    (job.autoComment ? 4 : 0) +
    (job.techNotes ? 4 : 0) +
    (job.photos.length > 0 ? 4 : 0) +
    (job.travel.totalMinutes > 0 ? 3 : 0) +
    (job.work.totalMinutes > 0 ? 3 : 0);
  return Math.min(100, checklistScore + flowScore + structureScore);
}

export function isJobActive(job: Job): boolean {
  return Boolean((job.travel.startedAt && !job.travel.endedAt) || (job.work.startedAt && !job.work.endedAt));
}

export function buildJobSearchText(job: Job): string {
  return [
    job.workOrder,
    job.address,
    job.customerName,
    job.phone,
    job.siteId,
    job.meterNumber,
    job.meterLocationDescription,
    job.techNotes,
    job.autoComment,
    ...job.riskNotes
  ]
    .join(' ')
    .toLowerCase();
}

export function buildLibrarySearchText(card: LibraryCard): string {
  return [card.title, card.when, ...card.steps, ...card.fails, ...card.refs, ...card.tags]
    .join(' ')
    .toLowerCase();
}

export function migrateLegacyJob(raw: unknown): Job {
  const legacy = LegacyJobSchema.parse(raw);
  const migrated = createEmptyJob({
    id: legacy.identity?.job_id ?? createId('job'),
    date: legacy.identity?.date ?? todayDate(),
    territory: legacy.identity?.territory ?? '',
    status: legacy.identity?.status ?? 'Open',
    appointmentType: legacy.service_info?.appointment_type ?? '',
    premiseStatus: legacy.service_info?.premis_status ?? '',
    address: legacy.service_info?.address ?? '',
    customerName: legacy.service_info?.customer_name ?? '',
    phone: legacy.service_info?.phone ? normalizePhone(legacy.service_info.phone) : '',
    workOrder: legacy.work_order?.work_order ?? '',
    siteId: legacy.work_order?.site_id_psid ?? '',
    hostOrder: legacy.work_order?.host_order_dis ?? '',
    hostCreatedAt: legacy.work_order?.host_time_created ?? '',
    meterNumber: legacy.meter?.meter_number ?? '',
    meterLocationCode: legacy.meter?.meter_location_code ?? '',
    meterLocationDescription: legacy.meter?.meter_location_desc ?? '',
    prevReading: legacy.meter?.prev_reading ?? null,
    currentReading: legacy.meter?.current_reading ?? null,
    prevReadDate: legacy.meter?.prev_read_date ?? '',
    travel: {
      startedAt: legacy.timing?.trv_start ?? null,
      endedAt: legacy.timing?.trv_end ?? null,
      totalMinutes: legacy.timing?.auto_durations?.trv_minutes ?? 0
    },
    work: {
      startedAt: legacy.timing?.wrk_start ?? null,
      endedAt: legacy.timing?.wrk_end ?? null,
      totalMinutes: legacy.timing?.auto_durations?.wrk_minutes ?? 0
    },
    checklist: ChecklistSchema.parse(legacy.checklist ?? {}),
    autoComment: legacy.comments?.auto_comment ?? '',
    techNotes: legacy.comments?.tech_notes ?? '',
    auditTrail: (legacy.audit_timeline ?? []).map((event) =>
      createAuditEvent('import', `${event.type}: ${event.detail}`, 'system')
    )
  });
  return hydrateJob(migrated, { touch: true });
}

export function migrateLegacyLibraryCard(raw: unknown): LibraryCard {
  const legacy = LegacyLibraryCardSchema.parse(raw);
  return LibraryCardSchema.parse({
    id: legacy.id,
    title: legacy.title,
    when: legacy.when,
    steps: legacy.steps,
    fails: legacy.fails,
    refs: legacy.refs,
    tags: legacy.tags,
    favorite: legacy.favorite,
    system: false,
    updatedAt: legacy.updated_at,
    lastViewedAt: null
  });
}
